<script lang="ts">
	import { createSvelteTable, renderComponent, FlexRender } from '$lib/components/ui/data-table';
	import type {
		SelectCart,
		SelectCartItem,
		SelectCourseOffering,
		SelectCourseOfferingDate,
		SelectLocation,
		SelectLocationGroup
	} from '$db/tables';
	import type { addToCartSchema } from '$lib/validations/cart/add-to-cart-schema';
	import {
		getCoreRowModel,
		getFilteredRowModel,
		getPaginationRowModel,
		type Column,
		type ColumnDef,
		type ColumnFiltersState,
		type PaginationState
	} from '@tanstack/table-core';
	import type { Infer, SuperValidated } from 'sveltekit-superforms';
	import * as Table from '$lib/components/ui/table/index.js';
	import AddToCartButton from './add-to-cart-button.svelte';
	import TypeCell from './type-cell.svelte';
	import Dates from './dates.svelte';
	import DataTableToolbar from './data-table-toolbar.svelte';
	import Button from '$lib/components/ui/button/button.svelte';
	import ChevronDown from '@lucide/svelte/icons/chevron-down';
	import type { removeFromCartSchema } from '$lib/validations/cart/remove-from-cart-schema';

	type TableData = SelectCourseOffering & { count: number } & {
		dates: (SelectCourseOfferingDate & {
			loc?: (SelectLocation & { locGroup?: SelectLocationGroup | null }) | null;
		})[];
	};

	interface Props {
		data: (SelectCourseOffering & { count: number } & {
			dates: (SelectCourseOfferingDate & {
				loc?: (SelectLocation & { locGroup?: SelectLocationGroup | null }) | null;
			})[];
		})[];
		cart: {
			cart: SelectCart;
			cartItems: SelectCartItem[];
		};
		addToCartForm: SuperValidated<Infer<typeof addToCartSchema>>;
		removeFromCartForm: SuperValidated<Infer<typeof removeFromCartSchema>>;
		sportspass: boolean;
	}

	let { data, cart, addToCartForm, removeFromCartForm, sportspass }: Props = $props();

	let columnFilters = $state<ColumnFiltersState>([]);
	let pagination = $state<PaginationState>({
		pageIndex: 0,
		pageSize: 5
	});

	const defaultColumns: ColumnDef<TableData>[] = [
		{
			accessorKey: 'type',
			header: 'Standort',
			cell: (info) =>
				renderComponent(TypeCell, {
					type: info.row.original.type,
					dates: info.row.original.dates
				})
		},
		{
			accessorKey: 'dates',
			header: 'Termine',
			cell: (info) =>
				renderComponent(Dates, {
					courseOfferingDates: info.row.original.dates
				})
		},
		{
			id: 'locationGroup',
			accessorFn: (row) =>
				Array.from(new Set(row.dates.map((date) => date.loc?.locGroup?.name ?? 'Live-Streaming'))),
			header: 'Standortgruppe',
			enableHiding: true,
			filterFn: 'arrIncludesSome'
		},
		{
			id: 'year',
			accessorFn: (row) =>
				Array.from(new Set(row.dates.map((d) => new Date(d.date).getFullYear()))),
			header: 'Jahr',
			enableHiding: true,
			filterFn: 'includesString'
		},
		{
			id: 'addToCart',
			header: '',
			cell: (info) =>
				renderComponent(AddToCartButton, {
					cart,
					addToCartForm,
					removeFromCartForm,
					courseOfferingId: info.row.original.id,
					currentCount: info.row.original.count,
					maxParticipants: info.row.original.maxParticipants,
					sportspass
				})
		}
	];

	const table = createSvelteTable({
		get data() {
			return data;
		},
		columns: defaultColumns,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: {
			columnPinning: {
				right: ['addToCart']
			},
			columnVisibility: {
				locationGroup: false,
				year: false
			}
		},
		state: {
			get columnFilters() {
				return columnFilters;
			},
			get pagination() {
				return pagination;
			}
		},
		onPaginationChange: (updater) => {
			if (typeof updater === 'function') {
				pagination = updater(pagination);
			} else {
				pagination = updater;
			}
		},
		onColumnFiltersChange: (updater) => {
			if (typeof updater === 'function') {
				columnFilters = updater(columnFilters);
			} else {
				columnFilters = updater;
			}
		}
	});

	function getPinningStyles(column: Column<TableData>) {
		return `
			position: ${column.getIsPinned() ? 'sticky' : 'relative'};
			left: ${column.getIsPinned() === 'left' ? column.getStart('left') + 'px' : 'unset'};
			right: ${column.getIsPinned() === 'right' ? column.getAfter('right') + 'px' : 'unset'};
			box-shadow: ${
				column.getIsPinned() === 'left'
					? '-4px 0 4px -4px gray inset'
					: column.getIsPinned() === 'right'
						? '4px 0 4px -4px gray inset'
						: 'none'
			};
			z-index: ${column.getIsPinned() ? 1 : 0};
			${column.getIsPinned() ? 'background: rgb(241 249 236);' : ''}
    `;
	}

	const locations = $derived.by(() =>
		Array.from(
			new Set(data.flatMap((v) => v.dates.map((v) => v.loc?.locGroup?.name ?? 'Live-Streaming')))
		)
	);
	const years = $derived.by(() =>
		Array.from(new Set(data.flatMap((v) => v.dates.map((d) => new Date(d.date).getFullYear()))))
	);
</script>

<div class="flex flex-col gap-4">
	<DataTableToolbar {table} {locations} {years} />
	<div class="relative w-full overflow-clip rounded-md bg-asge-50">
		<Table.Root>
			<Table.Header>
				{#each table.getHeaderGroups() as headerGroup}
					<Table.Row class="border-asge-100 hover:bg-secondary">
						{#each headerGroup.headers as header}
							<Table.Head style={getPinningStyles(header.column)}>
								{#if !header.isPlaceholder}
									<FlexRender
										content={header.column.columnDef.header}
										context={header.getContext()}
									/>
								{/if}
							</Table.Head>
						{/each}
					</Table.Row>
				{/each}
			</Table.Header>
			<Table.Body>
				{#each table.getRowModel().rows as row}
					<Table.Row class="border-asge-100 hover:bg-secondary">
						{#each row.getVisibleCells() as cell}
							<Table.Cell style={getPinningStyles(cell.column)}>
								<FlexRender content={cell.column.columnDef.cell} context={cell.getContext()} />
							</Table.Cell>
						{/each}
					</Table.Row>
				{/each}
			</Table.Body>
		</Table.Root>
	</div>
	{#if table.getPageCount() > 1}
		<div class="flex justify-center">
			<Button variant="ghost" onclick={() => table.setPageSize((v) => v + 5)}
				><ChevronDown /> Mehr Termine zeigen</Button
			>
		</div>
	{/if}
</div>
